/** Big Rational - Represents a rational number as `n/d`, with unlimited precision. */
export interface BRat {
  n: bigint;
  d: bigint;
}

export const BIG_INT_0 = BigInt(0);
export const BIG_INT_1 = BigInt(1);

/** Constructs a new Big Rational number `num/den`.
 * Does not simplify. */
export function makeBRat(num: bigint, den = BIG_INT_1): BRat {
  return { n: num, d: den };
}

export function makeBRatFromNumbers(num: number, den = 1): BRat {
  return makeBRat(BigInt(num), BigInt(den));
}

/** Sets Big Rational number `r` to `num/den`.
 * Does not simplify. */
export function setBRat(r: BRat, num: bigint, den: bigint): BRat {
  r.n = num;
  r.d = den;
  return r;
}

export function copyBRat(r: BRat, s: Readonly<BRat>): BRat {
  r.n = s.n;
  r.d = s.d;
  return r;
}

/** Simplifies big rational `r` in place by removing common factors of `n` and `d`,
 * and ensuring at most one negative, `n`. */
export function simpBRat(r: BRat): BRat {
  const g = gcdBInt(r.n, r.d);
  if (g !== BIG_INT_1) {
    r.n /= g;
    r.d /= g;
  }
  if (r.d < 0) {
    r.n = -r.n;
    r.d = -r.d;
  }
  return r;
}

/** Computes the greatest common divisor of bigints `a` and `b`.
 * This is never a negative number. */
export function gcdBInt(a: bigint, b: bigint): bigint {
  // Choose (a, b) so |a| >= |b|.
  const aA = a >= 0 ? a : -a;
  const bA = b >= 0 ? b : -b;
  let t: bigint | undefined;
  if (aA < bA) {
    t = a;
    a = b;
    b = t;
  }
  while (b !== BIG_INT_0) {
    // TODO: Implement the least-remainder version.
    t = b;
    // In JavaScript, a % b has the same sign as a.
    b = a % b;
    a = t;
  }
  return a >= 0 ? a : -a;
}

/** Adds Big Rational `s` to `r`, simplifying. */
export function addBRat(r: BRat, s: Readonly<BRat>): BRat {
  const { n: nr, d: dr } = r;
  const { n: ns, d: ds } = s;
  if (dr === ds) {
    r.n = nr + ns;
  } else {
    // Find least common multiple of denominators
    // and reuce the size of numbers multiplied for performance.
    // Factor denominators by their gcd g.
    // nr/dr + ns/ds
    // =>
    // 1   nr     ns
    // - (---- + ----)
    // g  dr/g   ds/g
    // =>
    // nr*ds/g + ns*dr/g
    // -----------------
    //     dr*ds/g
    // E.g. 1/9 + 5/12
    // => 1/3*(1/(9/3) + 5/(12/3)
    // => 1/3*(1/3 + 5/4)
    // => 1/3*(1*4 + 5*3)/(3*4)
    // => 19/36
    const g = gcdBInt(dr, ds);
    const dr_g = dr / g;
    const ds_g = ds / g;
    r.n = nr * ds_g + ns * dr_g;
    r.d = dr_g * ds;
  }
  return simpBRat(r);
}

/** Subtracts Big Rational `s` from `r`, simplifying. */
export function subBRat(r: BRat, s: Readonly<BRat>): BRat {
  const { n: nr, d: dr } = r;
  const { n: ns, d: ds } = s;
  if (dr === ds) {
    r.n = nr - ns;
  } else {
    const g = gcdBInt(dr, ds);
    const dr_g = dr / g;
    const ds_g = ds / g;
    r.n = nr * ds_g - ns * dr_g;
    r.d = dr_g * ds;
  }
  return simpBRat(r);
}

/** Negates the Big Rational `r`, simplifying signs so at most `n` is negative. */
export function negBRat(r: BRat): BRat {
  r.n = -r.n;
  if (r.d < 0) {
    r.n = -r.n;
    r.d = -r.d;
  }
  return r;
}

/** Multiplies Big Rational `r` by `s`, modifying `r`. */
export function mulBRat(r: BRat, s: Readonly<BRat>): BRat {
  r.n *= s.n;
  r.d *= s.d;
  return simpBRat(r);
}

/** Divides Big Rational `r` by `s`, modifying `r`. */
export function divBrat(r: BRat, s: Readonly<BRat>): BRat {
  r.n *= s.d;
  r.d *= s.n;
  return simpBRat(r);
}
